import ActionPlanBadge from '@/app/components/action-plan/components/common/action-plan-badge';
import { Intervention } from '@/app/components/action-plan/types';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import Markdown from '@/components/markdown/markdown';
import SourceQuoteTag from '@/components/source-quote/source-quote';
import { formatRiskDecimal, getCancerRiskSuffix, normalizeCancerTypeName } from '@/utils';
import { Box, CloseButton, Divider, Flex, Modal, Text } from '@cmpsr/components';
import { useModalInterventionDetailsController } from './modal-intervention-details.controller';

type ModalInterventionDetailsProps = {
  intervention: Intervention;
  isOpen: boolean;
  onClose: () => void;
};

export default function ModalInterventionDetails({ isOpen, onClose, intervention }: ModalInterventionDetailsProps) {
  const {
    title,
    whyItMattersFullExplanation,
    riskImpactDescription,
    isNonQuantifiable,
    howToAccomplish,
    cancersAffected,
    riskImpactList,
    reducedRisk,
    visibleSources,
    showLoadMoreButton,
    handleLoadMoreSources,
    headerText,
    riskText,
    sources,
  } = useModalInterventionDetailsController(intervention, isOpen);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <Modal.Overlay bg="background-overlay" backdropFilter="auto" />
      <Modal.Content
        position="relative"
        borderRadius="radii-2"
        maxW={{ base: 'full', lg: '854px' }}
        maxH={{ base: 'full', lg: 'calc(100% - 7.5rem);' }}
      >
        <Modal.Body backgroundColor="background-page" p={5}>
          <CloseButton
            borderRadius="50%"
            backgroundColor="background-action-default"
            onClick={onClose}
            position="absolute"
            top={5}
            right={5}
          />

          <Box
            width="max-content"
            px={3}
            py={2}
            backgroundColor="background-light"
            borderRadius="radii-1"
            mr={{ base: 5, lg: 6 }}
          >
            <Text variant="text-body-meta-medium" color="text-link-primary-default" fontWeight={600}>
              {headerText}
            </Text>
          </Box>

          <Flex direction="column" gap={6}>
            <Box as="header">
              <Text variant="text-header-2XL" mt={7} mb={3}>
                {title}
              </Text>

              <Flex alignItems="center" gap={3}>
                <ActionPlanBadge
                  text={formatRiskDecimal(reducedRisk.impact)}
                  isNonQuantifiable={isNonQuantifiable}
                  py={1}
                  px={2}
                  borderRadius="radii-1"
                />
                <Text variant="text-body-regular">{riskText}</Text>
              </Flex>
            </Box>

            <Box
              backgroundColor="background-light"
              borderRadius="radii-3"
              p={5}
              border="1px solid"
              borderColor="ui-element-divider"
            >
              <IconCatch icon="why-it-matters" size="m" mb={4} />
              <Text variant="text-body-display-XS" mb={1} fontWeight={600} letterSpacing={0}>
                Why it matters
              </Text>
              <Markdown>{whyItMattersFullExplanation}</Markdown>
            </Box>

            <Box
              backgroundColor="background-light"
              borderRadius="radii-3"
              p={5}
              border="1px solid"
              borderColor="ui-element-divider"
            >
              <IconCatch icon="risk-impact" size="m" mb={4} />
              <Text variant="text-body-display-XS" mb={4} fontWeight={600} letterSpacing={0}>
                Risk Impact
              </Text>
              <Text variant="text-body-regular" mb={4}>
                {riskImpactDescription}
              </Text>
              {!isNonQuantifiable && (
                <>
                  {riskImpactList.map(({ impact, cancerType }, index) => (
                    <Flex alignItems="center" key={index} gap={3} mt={2}>
                      <ActionPlanBadge
                        text={formatRiskDecimal(reducedRisk.impact)}
                        py={1}
                        px={2}
                        borderRadius="radii-1"
                      />
                      <Text variant="text-body-regular">
                        {cancersAffected.length > 0
                          ? `Reduce your ${normalizeCancerTypeName(cancerType)}${getCancerRiskSuffix(cancerType)} risk`
                          : 'Reduce your overall cancer risk'}
                      </Text>
                    </Flex>
                  ))}
                </>
              )}
            </Box>

            <Box
              backgroundColor="background-light"
              borderRadius="radii-3"
              p={5}
              border="1px solid"
              borderColor="ui-element-divider"
            >
              <IconCatch icon="how-to-accomplish" size="m" mb={4} />
              <Text variant="text-body-display-XS" mb={1} fontWeight={600} letterSpacing={0}>
                How to do it
              </Text>
              <Box pl={4} color="text-primary">
                <Markdown>{howToAccomplish}</Markdown>
              </Box>
            </Box>
          </Flex>
          {sources && sources.length ? (
            <>
              <Divider pt={8} />
              <Flex direction="column" w="full" borderRadius="radii-3">
                <Text textStyle="text-header-M" pt={7} pb={7} letterSpacing={0}>
                  The science
                </Text>
                <SourceQuoteTag
                  sources={sources.slice(0, visibleSources)}
                  showLoadMoreButton={showLoadMoreButton}
                  loadMore={handleLoadMoreSources}
                />
              </Flex>
            </>
          ) : null}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
}
